import { z } from 'zod';
import { ProposalLegKindSchema } from './bank-line-proposal.js';
import { ClassificationOutcomeSchema, createPaginatedResponseSchema } from './common.js';
// =============================================================================
// Classification (admin read-only observability surface)
// =============================================================================
//
// The wire shapes for the super-admin classification dashboard. Deliberately
// PII-minimal: a decision's candidate keys and payloads are opaque strings and
// free-form JSON that a consuming domain computed, so they can embed ids and
// anything else. Nothing here carries a client name, a line narration or an
// email subject; a row names its account by id and the dashboard links out to
// the account rather than embedding the customer's data.
//
// The four rates are DEFINED by `ProblemSummary` in the classification domain
// and computed by `summariseVerdicts`. They travel on the wire as numbers so
// the dashboard renders what the backend derived; no consumer recomputes one
// from the verdict counts beside it.
/** The problems the platform records decisions for. Mirrors `ClassificationProblem`. */
export const ClassificationProblemSchema = z.enum([
    'BANK_LINE_ALLOCATION',
    'EMAIL_CLIENT_MATTER',
    'EMAIL_BILLABLE',
    'EMAIL_SUPPLIER_INVOICE',
    'CALENDAR_EVENT_CLIENT_MATTER',
    'EXPENSE_GL_ACCOUNT',
]);
/** Whether a producer's decisions drive product behaviour or only run alongside. */
export const ClassificationModeSchema = z.enum(['LIVE', 'SHADOW']);
/**
 * What caused a decision to be recorded: `initial` (the ordinary first
 * pass), `sweep` (a periodic re-evaluation over diffuse history drift), or
 * `event:<name>` (a specific domain event re-triggered classification, e.g.
 * `event:supplier-invoice.approved`). The event suffix is open-ended - new
 * triggers are a producer's own call, not a registry line here - so this is
 * a union rather than a closed `z.enum`, unlike `ClassificationProblemSchema`.
 */
export const ClassificationTriggerSchema = z.union([
    z.literal('initial'),
    z.literal('sweep'),
    z.string().regex(/^event:.+$/, { error: 'a trigger must be `initial`, `sweep`, or `event:<name>`' }),
]);
// Defined in `common.ts` (see the note there on the `bank-line-proposal.ts`
// import cycle) and re-exported here so callers keep importing it from the
// classification namespace.
export { ClassificationOutcomeSchema } from './common.js';
/** Who supplied a truth fact. */
export const ClassificationTruthSourceSchema = z.enum(['USER', 'AGENT', 'SYSTEM']);
/**
 * One decision's standing against its subject's truth. Derived on read, never
 * stored - see the verdict table in the classification domain README for what
 * each value means.
 */
export const ClassificationVerdictSchema = z.enum([
    'PENDING',
    'CORRECT',
    'RANKING_MISS',
    'RETRIEVAL_MISS',
    'ABSTAIN_MISS',
    'CORRECT_ABSTAIN',
    'WRONG_SUGGESTION',
    'REJECTED_ABSTAIN',
]);
/** Counts by verdict. Every verdict is present, so a reader never branches on absence. */
export const ClassificationVerdictCountsSchema = z.object({
    PENDING: z.number().int(),
    CORRECT: z.number().int(),
    RANKING_MISS: z.number().int(),
    RETRIEVAL_MISS: z.number().int(),
    ABSTAIN_MISS: z.number().int(),
    CORRECT_ABSTAIN: z.number().int(),
    WRONG_SUGGESTION: z.number().int(),
    REJECTED_ABSTAIN: z.number().int(),
});
// -----------------------------------------------------------------------------
// GET /admin/classification/problems
// -----------------------------------------------------------------------------
/**
 * - `problem`: which classifier's record to summarise (required - the rates are
 *   only comparable within one problem).
 * - `from`/`to`: ISO datetime bounds on `decidedAt` (`from` inclusive, `to` exclusive).
 * - `mode` / `producer` / `accountId`: narrow to one slice; omit `producer` to get
 *   a row per producer that decided in the window, which is how the dashboard
 *   discovers them (each consuming domain keeps its own producer registry).
 * - `bucket`: `day` additionally splits by UTC day, for the trend line.
 */
export const AdminClassificationProblemsQuerySchema = z.object({
    problem: ClassificationProblemSchema,
    from: z.iso.datetime({ error: 'from must be a valid ISO datetime string' }),
    to: z.iso.datetime({ error: 'to must be a valid ISO datetime string' }),
    mode: ClassificationModeSchema.optional(),
    producer: z.string().min(1).max(64).optional(),
    accountId: z.uuid().optional(),
    bucket: z.enum(['none', 'day']).prefault('none'),
});
/**
 * One producer's (and, when bucketed, one day's) figures. A rate is `null`,
 * never `0`, when its denominator is empty - the distinction between "we were
 * never right" and "nothing has been adjudicated yet" is the whole point.
 */
export const AdminClassificationProblemSummarySchema = z.object({
    problem: ClassificationProblemSchema,
    producer: z.string(),
    mode: ClassificationModeSchema,
    /** The UTC day as `YYYY-MM-DD`, or null when the bucket is `none`. */
    day: z.string().nullable(),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    decisions: z.number().int(),
    adjudicated: z.number().int(),
    byVerdict: ClassificationVerdictCountsSchema,
    /** SUGGESTED / decisions. */
    coverage: z.number().nullable(),
    /** CORRECT / (adjudicated SUGGESTED with a known answer). */
    agreement: z.number().nullable(),
    /** answer among candidates / (adjudicated with a known non-NONE answer). */
    recall: z.number().nullable(),
    /** ABSTAIN_MISS / (adjudicated ABSTAINED with a known non-NONE answer). */
    abstainMiss: z.number().nullable(),
});
export const AdminClassificationProblemsResponseSchema = z.object({
    summaries: z.array(AdminClassificationProblemSummarySchema),
});
// -----------------------------------------------------------------------------
// GET /admin/classification/decisions
// -----------------------------------------------------------------------------
export const AdminClassificationDecisionsQuerySchema = z.object({
    problem: ClassificationProblemSchema,
    from: z.iso.datetime({ error: 'from must be a valid ISO datetime string' }).optional(),
    to: z.iso.datetime({ error: 'to must be a valid ISO datetime string' }).optional(),
    mode: ClassificationModeSchema.optional(),
    producer: z.string().min(1).max(64).optional(),
    accountId: z.uuid().optional(),
    /** Include decisions a later re-run displaced. Defaults to active only. */
    includeSuperseded: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
    /** Only decisions a human has since adjudicated (verdict is not PENDING). */
    adjudicatedOnly: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(50),
});
/**
 * A decision as the table renders it: enough to scan and sort, without the
 * candidate detail. Open the drill-down for that.
 */
export const AdminClassificationDecisionRowSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    problem: ClassificationProblemSchema,
    subjectType: z.string(),
    subjectId: z.string(),
    mode: ClassificationModeSchema,
    producer: z.string(),
    outcome: ClassificationOutcomeSchema,
    verdict: ClassificationVerdictSchema,
    /** What caused this decision to be recorded. Null on a row recorded before this column existed. */
    trigger: ClassificationTriggerSchema.nullable(),
    candidateCount: z.number().int(),
    /** The rank-1 candidate's score, or null when there were no candidates. */
    topScore: z.number().nullable(),
    /** Rank-1 score minus rank-2 score, or null when there is no rank-2 candidate. */
    margin: z.number().nullable(),
    decidedAt: z.iso.datetime(),
    supersededAt: z.iso.datetime().nullable(),
});
export const AdminClassificationDecisionListResponseSchema = createPaginatedResponseSchema(AdminClassificationDecisionRowSchema);
// -----------------------------------------------------------------------------
// GET /admin/classification/decisions/:decisionId
// -----------------------------------------------------------------------------
export const AdminClassificationDecisionParamsSchema = z.object({
    decisionId: z.uuid(),
});
/**
 * The detail read needs the account too: a decision id is unique, but every
 * read of the record is account-scoped so a mistyped id cannot walk the table.
 */
export const AdminClassificationDecisionDetailQuerySchema = z.object({
    accountId: z.uuid(),
});
/**
 * One ranked candidate. `key` is the problem's own identity string and `payload`
 * is its own JSON - both opaque here, and both able to embed ids, which is why
 * this surface is super-admin only.
 */
export const AdminClassificationCandidateSchema = z.object({
    id: z.string(),
    rank: z.number().int(),
    key: z.string(),
    score: z.number(),
    features: z.object({}).catchall(z.number()),
    payload: z.unknown(),
});
export const AdminClassificationTruthSchema = z.object({
    answerKey: z.string().nullable(),
    answerPayload: z.unknown(),
    rejectedKeys: z.array(z.string()),
    source: ClassificationTruthSourceSchema,
    acceptedSuggestion: z.boolean(),
    recordedByUserId: z.string().nullable(),
    recordedAt: z.iso.datetime(),
});
export const AdminClassificationDecisionDetailSchema = AdminClassificationDecisionRowSchema.extend({
    versions: z.object({
        retriever: z.string(),
        featuriser: z.string(),
        scorer: z.string(),
        policy: z.string(),
    }),
    subjectFeatures: z.object({}).catchall(z.number()),
    candidates: z.array(AdminClassificationCandidateSchema),
    truth: AdminClassificationTruthSchema.nullable(),
});
// -----------------------------------------------------------------------------
// GET /admin/classification/decisions/:decisionId/explanation
// -----------------------------------------------------------------------------
/**
 * The plain-language sentence for one decision, or the honest reason there is
 * none. A union rather than a bare nullable string because "where the
 * sentence comes from" and "why there is none" both matter to the dashboard:
 *
 * - `explained`: the sentence, derived from the stored rank-1 candidate's own
 *   legs and feature vector through the producer's explainer - a snapshot of
 *   the evidence at decision time, reproducible from the row alone (the
 *   forest producer's shape).
 * - `stored`: the sentence IS the linked proposal's stored per-leg
 *   `matchReason` - read it off `presented` (the cascade producer's shape:
 *   its explainer needs the full allocation context, and its LIVE sentence is
 *   already persisted verbatim on the proposal, so nothing is recomputed).
 * - `not-available`: a cascade decision with no linked proposal, so there is
 *   no stored sentence to show (a state the LIVE path should not produce, but
 *   represented honestly rather than guessed at).
 * - `abstained`: the decision made no suggestion, so there is nothing to
 *   explain (the row-level `candidateCount`/`topScore` still describe what
 *   was considered).
 * - `unknown-producer`: the decision was recorded by a producer no longer in
 *   the allocation registry, so no explainer is bound for it.
 */
export const AdminClassificationExplanationSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('explained'), sentence: z.string() }),
    z.object({ status: z.literal('stored') }),
    z.object({ status: z.literal('not-available') }),
    z.object({ status: z.literal('abstained') }),
    z.object({ status: z.literal('unknown-producer'), producer: z.string() }),
]);
/**
 * One decision's user-facing prose. `presented` is what a user actually saw
 * (the proposal built from this decision, with each leg's stored
 * `matchReason`); `explanation` says what the sentence for this decision is
 * and where it comes from. For a SHADOW forest decision `presented` is null by
 * construction (a shadow never builds a proposal) and `explanation` carries
 * the feature-derived sentence - the only way to read what the shadow WOULD
 * present, which is the point of this read. BANK_LINE_ALLOCATION only - other
 * problems have no explainer bound and the route rejects them with 400.
 */
export const AdminClassificationDecisionExplanationSchema = z.object({
    decisionId: z.string(),
    producer: z.string(),
    outcome: ClassificationOutcomeSchema,
    candidateCount: z.number().int(),
    /** The rank-1 candidate's score, or null when there were no candidates. */
    topScore: z.number().nullable(),
    explanation: AdminClassificationExplanationSchema,
    /**
     * What was presented to the user: the proposal built from this decision, if
     * one was. Null for shadow decisions and abstains (no proposal exists).
     * Includes a since-dismissed proposal - it was still presented.
     */
    presented: z
        .object({
        /** When the scorer computed the proposal. Null on a legacy row that predates the column. */
        computedAt: z.iso.datetime().nullable(),
        legs: z.array(z.object({ id: z.string(), kind: ProposalLegKindSchema, matchReason: z.string().nullable() })),
    })
        .nullable(),
});
// -----------------------------------------------------------------------------
// GET /admin/classification/subjects/:subjectId/decisions
// -----------------------------------------------------------------------------
/**
 * `subjectId` is the consuming domain's own id (a bank line id, a stored email
 * id) - a string, not necessarily a UUID, because the classification record is
 * deliberately blind to what a subject is.
 */
export const AdminClassificationSubjectParamsSchema = z.object({
    subjectId: z.string().min(1).max(128),
});
/**
 * `problem` is required alongside `accountId` because a subject id is only
 * meaningful within one problem's subject space - the same line id never
 * carries decisions for two problems, but nothing in the record enforces that,
 * and the read should not rely on it.
 */
export const AdminClassificationSubjectDecisionsQuerySchema = z.object({
    accountId: z.uuid(),
    problem: ClassificationProblemSchema,
});
/**
 * Every producer's ACTIVE decision for one subject - the per-line comparison
 * read. Same row shape as the decisions list; at most one row per producer
 * (supersession is keyed per producer).
 */
export const AdminClassificationSubjectDecisionsResponseSchema = z.object({
    decisions: z.array(AdminClassificationDecisionRowSchema),
});
// -----------------------------------------------------------------------------
// GET /admin/classification/decisions/:decisionId/subject
// -----------------------------------------------------------------------------
/**
 * The thing a decision was about, resolved back to something a person
 * recognises: the bank line, the email, the calendar event.
 *
 * The classification record itself cannot answer this. It stores a
 * `subjectType`/`subjectId` pair and is deliberately blind to what those mean -
 * that blindness is what lets a new classifier be one registry line rather than
 * a schema change. So the resolution happens above the domain, and this union
 * is its result: one variant per subject type the platform records, plus the
 * `UNRESOLVED` case for a subject that has since been deleted, or a subject
 * type nothing resolves yet.
 *
 * Every variant carries display content the account owns (a line description, an
 * email subject), which is why the route behind it is super-admin only - the
 * same reason the decision detail read is.
 */
export const AdminClassificationSubjectSchema = z.discriminatedUnion('subjectType', [
    z.object({
        subjectType: z.literal('BANK_STATEMENT_LINE'),
        subjectId: z.string(),
        date: z.iso.datetime(),
        description: z.string(),
        amountMinorUnits: z.number().int(),
        currency: z.string(),
        bankAccountLabel: z.string(),
        /** True once the line has been allocated to a ledger entry set. */
        allocated: z.boolean(),
    }),
    z.object({
        subjectType: z.literal('STORED_EMAIL'),
        subjectId: z.string(),
        date: z.iso.datetime(),
        subject: z.string(),
        snippet: z.string(),
        direction: z.enum(['sent', 'received']),
        from: z.object({ name: z.string().nullable(), email: z.string() }),
        to: z.array(z.object({ name: z.string().nullable(), email: z.string() })),
    }),
    z.object({
        subjectType: z.literal('STORED_CALENDAR_EVENT'),
        subjectId: z.string(),
        subject: z.string().nullable(),
        location: z.string().nullable(),
        organiserEmail: z.string().nullable(),
        attendeeCount: z.number().int(),
        cancelled: z.boolean(),
    }),
    z.object({
        subjectType: z.literal('UNRESOLVED'),
        subjectId: z.string(),
        /** The subjectType the decision recorded, kept so the reader still knows what it was. */
        recordedSubjectType: z.string(),
        reason: z.enum(['not-found', 'no-resolver']),
    }),
]);
